import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../../hooks/useBranding';
import { Users, Search, ChevronLeft, ChevronRight, Shield, AlertCircle, Plus, Eye, EyeOff, CheckCircle, Lock, Unlock } from 'lucide-react';
import api, { adminRolesAPI } from '../../services/api';

// Per-slug accent colors for known system roles. Anything not in this map
// falls back to purple (internal) or blue (customer) via roleBadgeClass so
// custom roles created in /admin/roles still get a legible badge.
const ROLE_ACCENT_COLORS = {
  admin: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  finance: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  technician: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
  company_admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  user: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
};

const roleBadgeClass = (role) => {
  if (!role) return 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300';
  if (ROLE_ACCENT_COLORS[role.name]) return ROLE_ACCENT_COLORS[role.name];
  return role.isInternal
    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300';
};

export default function UserManagementPage() {
  const { portalName } = useBranding();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
  });

  // Roles come from /api/admin/roles so the two admin surfaces (this page and
  // /admin/roles) stay in sync — including the `finance` system role and any
  // custom roles created via the roles UI. Sorted by hierarchy_level ASC on
  // the server; we preserve that ordering.
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminRolesAPI.list()
      .then((res) => {
        if (!cancelled) setRoles(res.data.roles || []);
      })
      .catch(() => {
        if (!cancelled) setRoles([]);
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const roleByName = useMemo(
    () => Object.fromEntries(roles.map(r => [r.name, r])),
    [roles]
  );
  const internalRoles = useMemo(() => roles.filter(r => r.isInternal), [roles]);
  const customerRoles = useMemo(() => roles.filter(r => !r.isInternal), [roles]);

  // Role change modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  const [roleChangeError, setRoleChangeError] = useState(null);

  // Create user modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });

      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);

      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1);
  }, [searchTerm, roleFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1);
  };

  const openRoleChangeModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleChangeError(null);
  };

  const closeRoleChangeModal = () => {
    setSelectedUser(null);
    setNewRole('');
    setRoleChangeError(null);
  };

  const handleRoleChange = async () => {
    if (!selectedUser || newRole === selectedUser.role) {
      closeRoleChangeModal();
      return;
    }

    setRoleChangeLoading(true);
    setRoleChangeError(null);

    try {
      await api.patch(`/admin/users/${selectedUser.id}/role`, { role: newRole });

      // Update user in local state. is_internal_staff comes from the fetched
      // role definition, so finance and custom internal roles flip the bit too.
      const newIsInternal = roleByName[newRole]?.isInternal ?? false;
      setUsers(users.map(u =>
        u.id === selectedUser.id
          ? { ...u, role: newRole, is_internal_staff: newIsInternal }
          : u
      ));

      closeRoleChangeModal();
    } catch (err) {
      setRoleChangeError(err.response?.data?.error || 'Failed to change role');
    } finally {
      setRoleChangeLoading(false);
    }
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setCreateForm({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: '',
    });
    setCreateError(null);
    setCreateSuccess(false);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: '',
    });
    setCreateError(null);
    setCreateSuccess(false);
    setShowPassword(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);

    try {
      // An empty role means "decide from ConnectWise" (the server's mapping).
      await api.post('/admin/users', { ...createForm, role: createForm.role || undefined });

      setCreateSuccess(true);

      // Refresh user list after short delay
      setTimeout(() => {
        closeCreateModal();
        fetchUsers(1);
      }, 1500);

    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">User Management</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Manage portal users and their roles
          </p>
        </div>
        <button
          onClick={openCreateModal}
          disabled={rolesLoading}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          Create User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            disabled={rolesLoading}
            className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white disabled:opacity-60"
          >
            <option value="">{rolesLoading ? 'Loading roles…' : 'All Roles'}</option>
            {internalRoles.length > 0 && (
              <optgroup label="Internal Staff">
                {internalRoles.map(role => (
                  <option key={role.name} value={role.name}>{role.displayName}</option>
                ))}
              </optgroup>
            )}
            {customerRoles.length > 0 && (
              <optgroup label="Customer-Facing">
                {customerRoles.map(role => (
                  <option key={role.name} value={role.name}>{role.displayName}</option>
                ))}
              </optgroup>
            )}
          </select>
        </form>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      Loading users...
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400">
                    <Users className="w-8 h-8 mx-auto mb-2 text-neutral-400 dark:text-neutral-500" />
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => {
                      sessionStorage.setItem('adminUsersReturnUrl', window.location.pathname + window.location.search);
                      navigate(`/admin/users/${user.id}`);
                    }}
                    className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${roleBadgeClass(roleByName[user.role])}`}>
                        {roleByName[user.role]?.displayName || user.role}
                      </span>
                      {user.is_internal_staff && (
                        <Shield className="inline w-4 h-4 ml-1 text-purple-500" title="Internal Staff" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">
                      {user.company_name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">
                      {formatDate(user.last_login)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                        user.email_verified
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                      }`}>
                        {user.email_verified ? 'Verified' : 'Unverified'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openRoleChangeModal(user);
                        }}
                        className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Change Role
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} users
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchUsers(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => fetchUsers(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Role Change Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={closeRoleChangeModal} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Change User Role
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
              Changing role for <strong>{selectedUser.first_name} {selectedUser.last_name}</strong> ({selectedUser.email})
            </p>

            {roleChangeError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {roleChangeError}
              </div>
            )}

            {rolesLoading ? (
              <div className="py-8 flex items-center justify-center gap-2 text-neutral-500 dark:text-neutral-400">
                <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                Loading roles…
              </div>
            ) : (
              <div className="mb-6 space-y-5">
                {internalRoles.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="w-4 h-4 text-purple-500" />
                      <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Internal Staff Roles</h4>
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                      {portalName} technicians / staff — access to the admin panel.
                    </p>
                    <div className="space-y-2">
                      {internalRoles.map((role) => (
                        <label
                          key={role.name}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            newRole === role.name
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                              : 'border-neutral-200 dark:border-neutral-600 hover:border-purple-300 dark:hover:border-purple-600'
                          }`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={role.name}
                            checked={newRole === role.name}
                            onChange={(e) => setNewRole(e.target.value)}
                            className="mt-0.5"
                          />
                          <div>
                            <p className="font-medium text-neutral-900 dark:text-white">{role.displayName}</p>
                            {role.description && (
                              <p className="text-sm text-neutral-500 dark:text-neutral-400">{role.description}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {customerRoles.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Unlock className="w-4 h-4 text-blue-500" />
                      <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Customer Roles</h4>
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                      Client company contacts — access to the customer portal only.
                    </p>
                    <div className="space-y-2">
                      {customerRoles.map((role) => (
                        <label
                          key={role.name}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            newRole === role.name
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                              : 'border-neutral-200 dark:border-neutral-600 hover:border-blue-300 dark:hover:border-blue-600'
                          }`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={role.name}
                            checked={newRole === role.name}
                            onChange={(e) => setNewRole(e.target.value)}
                            className="mt-0.5"
                          />
                          <div>
                            <p className="font-medium text-neutral-900 dark:text-white">{role.displayName}</p>
                            {role.description && (
                              <p className="text-sm text-neutral-500 dark:text-neutral-400">{role.description}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={closeRoleChangeModal}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleRoleChange}
                disabled={roleChangeLoading || newRole === selectedUser.role}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {roleChangeLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={closeCreateModal} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {createSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                  User Created Successfully
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  The user can now log in with their credentials.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                  Create New User
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                  Create a portal user without email verification. The email must belong to an existing ConnectWise contact.
                </p>

                {createError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2 text-red-700 dark:text-red-300 text-sm">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {createError}
                  </div>
                )}

                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                      placeholder="user@company.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={createForm.firstName}
                        onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={createForm.lastName}
                        onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={createForm.password}
                        onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                        placeholder="Minimum 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Role
                    </label>
                    <select
                      value={createForm.role}
                      onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                      disabled={rolesLoading}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white disabled:opacity-60"
                    >
                      {rolesLoading && <option>Loading roles…</option>}
                      <option value="">Decide from ConnectWise (member role, or the contact type mapping)</option>
                      {internalRoles.length > 0 && (
                        <optgroup label={`Internal Staff — ${portalName} technicians / staff`}>
                          {internalRoles.map(role => (
                            <option key={role.name} value={role.name}>
                              {role.displayName}{role.description ? ` — ${role.description}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {customerRoles.length > 0 && (
                        <optgroup label="Customer-Facing — client company users">
                          {customerRoles.map(role => (
                            <option key={role.name} value={role.name}>
                              {role.displayName}{role.description ? ` — ${role.description}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                      Internal roles grant admin-panel access; customer roles are for client contacts. Role may be auto-adjusted based on the ConnectWise contact type.
                    </p>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeCreateModal}
                      className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createLoading ? 'Creating...' : 'Create User'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
